
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { MapPin, AlertTriangle, Loader2, Filter } from 'lucide-react';
import { DateRange } from 'react-day-picker';
import { subDays, startOfDay, endOfDay } from 'date-fns';
import GoogleMap from './GoogleMap';
import LastLocationDisplay from './LastLocationDisplay';
import EnhancedDateRangeSelector from './EnhancedDateRangeSelector';
import { useBikeLocationHistory } from '@/hooks/useBikeLocationHistory';

interface LocationHistoryMapProps {
  bikeId?: string;
  courierId?: string;
  isAdmin: boolean;
  onSyncGPS?: () => Promise<void>;
  isSyncing?: boolean;
}

const LocationHistoryMap: React.FC<LocationHistoryMapProps> = ({
  bikeId,
  courierId,
  isAdmin,
  onSyncGPS,
  isSyncing = false
}) => {
  // Состояние для диапазона дат - по умолчанию последние 7 дней
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() => ({
    from: startOfDay(subDays(new Date(), 6)),
    to: endOfDay(new Date())
  }));

  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [lastLocationKey, setLastLocationKey] = useState(0);
  const [filterByMoscowRegion, setFilterByMoscowRegion] = useState(true); // По умолчанию включена фильтрация

  // Подготовка параметров для хука
  const dateFrom = dateRange?.from || subDays(new Date(), 6);
  const dateTo = dateRange?.to || new Date();

  const {
    locationHistory,
    loading,
    error,
    averageSpeed,
    refetch
  } = useBikeLocationHistory(
    bikeId,
    courierId,
    dateFrom,
    dateTo,
    refreshTrigger,
    filterByMoscowRegion
  );

  // Обработчик обновления данных
  const handleRefresh = useCallback(() => {
    console.log('Ручное обновление данных истории местоположений');
    setRefreshTrigger(prev => prev + 1);
  }, []);

  // Обработчик изменения диапазона дат
  const handleDateRangeChange = useCallback((range: DateRange | undefined) => {
    setDateRange(range);
  }, []);

  // Обработчик переключения фильтрации
  const handleFilterToggle = useCallback((checked: boolean) => {
    console.log('Переключение фильтрации по Московской области:', checked);
    setFilterByMoscowRegion(checked);
  }, []);

  // Обработчик синхронизации GPS с принудительным обновлением
  const handleSyncGPS = useCallback(async () => {
    if (onSyncGPS) {
      try {
        console.log('Начинаем синхронизацию GPS...');
        await onSyncGPS();
        console.log('GPS синхронизация завершена, обновляем данные истории местоположений');
        
        // Ожидаем немного, чтобы данные успели обновиться в БД
        setTimeout(() => {
          console.log('Принудительно обновляем историю местоположений после синхронизации GPS');
          setRefreshTrigger(prev => prev + 1);
          setLastLocationKey(prev => prev + 1);
        }, 2000);
      } catch (error) {
        console.error('Ошибка при синхронизации GPS:', error);
      }
    }
  }, [onSyncGPS]);

  // Эффект для автообновления при изменении параметров
  useEffect(() => {
    if (refreshTrigger > 0) {
      console.log('Принудительное обновление данных истории местоположений');
      setLastLocationKey(prev => prev + 1);
    }
  }, [refreshTrigger]);

  // Проверяем права доступа
  if (!isAdmin && !bikeId && !courierId) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          У вас нет прав для просмотра истории перемещений.
        </AlertDescription>
      </Alert>
    );
  }

  if (!bikeId && !courierId) {
    return (
      <Alert>
        <MapPin className="h-4 w-4" />
        <AlertDescription>
          Выберите велосипед или курьера для просмотра истории перемещений.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Панель управления */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            История перемещений
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <EnhancedDateRangeSelector
            dateRange={dateRange}
            onDateRangeChange={handleDateRangeChange}
            onRefresh={handleRefresh}
            isLoading={loading}
          />
          
          {/* Фильтр по Московской области */}
          <div className="flex items-center space-x-2 p-3 bg-muted/50 rounded-lg">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <Label htmlFor="moscow-filter" className="text-sm font-medium">
              Показывать только координаты в Московской области
            </Label>
            <Switch
              id="moscow-filter"
              checked={filterByMoscowRegion}
              onCheckedChange={handleFilterToggle}
            />
          </div>
          
          {filterByMoscowRegion && (
            <div className="text-xs bg-card border border-border p-3 rounded-md">
              <span className="text-foreground font-medium">Фильтр активен:</span>
              <span className="text-muted-foreground ml-1">
                Отображаются только координаты в пределах Московской области (54.2°-57.0° с.ш., 35.1°-40.8° в.д.)
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Индикатор загрузки */}
      {loading && (
        <Card>
          <CardContent className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            <span>Загрузка истории перемещений...</span>
          </CardContent>
        </Card>
      )}

      {/* Ошибка загрузки */}
      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Ошибка при загрузке данных: {error}
          </AlertDescription>
        </Alert>
      )}

      {/* Отсутствие данных */}
      {!loading && !error && (!locationHistory || locationHistory.length === 0) && (
        <Alert>
          <MapPin className="h-4 w-4" />
          <AlertDescription>
            {filterByMoscowRegion 
              ? 'Нет данных GPS в Московской области для выбранного периода. Попробуйте отключить фильтр или изменить диапазон дат.'
              : 'Нет данных GPS для выбранного периода. Попробуйте изменить диапазон дат или проверьте настройки GPS.'
            }
          </AlertDescription>
        </Alert>
      )}

      {/* Карта */}
      {locationHistory && locationHistory.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <GoogleMap
              locationHistory={locationHistory}
              isLoading={loading}
            />
          </CardContent>
        </Card>
      )}

      {/* Последнее местоположение */}
      {locationHistory && locationHistory.length > 0 && (
        <LastLocationDisplay 
          key={lastLocationKey}
          locationHistory={locationHistory}
          onSyncGPS={handleSyncGPS}
          isSyncing={isSyncing}
          bikeId={bikeId}
          courierId={courierId}
        />
      )}
    </div>
  );
};

export default LocationHistoryMap;
